import { Bubble } from '@ant-design/x';
import clsx from 'clsx';
import { memo, useMemo } from 'react';
import { AiChatDocumentMessagePayloadRoleEnum } from '../../enums/AiChatDocumentMessagePayloadRoleEnum';
import type { AiChatDocumentMessagePayload } from '../../interfaces/AiChatDocumentMessagePayload';
import { documentChatMessageKey, showDocumentChatMessageActions } from './documentChatActions';
import { DocumentChatMessageActions } from './DocumentChatMessageActions';
import { DocumentChatMessageItem } from './DocumentChatMessageItem';

/** User Bubble content — grow to text, cap width (AIGrant parity). */
export const USER_BUBBLE_CONTENT_STYLE = {
  background: '#193057',
  color: '#f8fafc',
  maxWidth: 'min(720px, 85%)',
  // ponytail: ant Bubble content defaults minWidth:0 + word-break:break-word;
  // with width:100% children the bubble's preferred width collapses to ~1ch.
  width: 'fit-content',
  minWidth: 'fit-content',
  borderBottomRightRadius: 0,
} as const;

export const ASSISTANT_BUBBLE_STYLES = {
  root: { width: '100%', maxWidth: '100%' },
  body: { width: '100%', maxWidth: '100%' },
  content: {
    background: 'transparent',
    boxShadow: 'none',
    paddingInline: 0,
    paddingBlock: 0,
    maxWidth: '100%',
    width: '100%',
    borderBottomLeftRadius: 0,
  },
} as const;

export const ERROR_BUBBLE_CONTENT_STYLE = {
  background: '#f73b3b',
  color: '#f0f0f0',
  borderBottomLeftRadius: 0,
  border: '1px solid #f73b3b',
} as const;

const bubbleRoleStyles = {
  user: {
    placement: 'end' as const,
    variant: 'filled' as const,
    shape: 'round' as const,
    styles: {
      content: USER_BUBBLE_CONTENT_STYLE,
    },
  },
  assistant: {
    placement: 'start' as const,
    variant: 'borderless' as const,
    styles: ASSISTANT_BUBBLE_STYLES,
  },
};

type Props = {
  messages: AiChatDocumentMessagePayload[];
  senderBusy: boolean;
  /** When false, parent owns scroll (stickToBottomRef) — Bubble.List autoScroll off. */
  autoScroll?: boolean;
  onRetryError: (index: number) => void;
  onRegenerate: (index: number) => void;
  bottomRef?: React.RefObject<HTMLDivElement | null>;
};

function buildBubbleItem(
  message: AiChatDocumentMessagePayload,
  idx: number,
  {
    hasPending,
    senderBusy,
    lastAssistantIndex,
    onRetryError,
    onRegenerate,
  }: {
    hasPending: boolean;
    senderBusy: boolean;
    lastAssistantIndex: number;
    onRetryError: Props['onRetryError'];
    onRegenerate: Props['onRegenerate'];
  },
) {
  const isUser = message.role === AiChatDocumentMessagePayloadRoleEnum.User;
  const isError = !!message.isError || message.status === 'Error';
  const role = isUser ? 'user' : 'assistant';
  const key = documentChatMessageKey(message, idx);
  const canAct = showDocumentChatMessageActions(message, { livePending: hasPending });
  const isLastAssistant = idx === lastAssistantIndex;

  const doneActions =
    canAct && !isError ? (
      <DocumentChatMessageActions
        content={message.content}
        disabled={senderBusy}
        onRetry={
          isLastAssistant
            ? () => {
                onRegenerate(idx);
              }
            : undefined
        }
      />
    ) : undefined;

  const roleStyles = isUser
    ? bubbleRoleStyles.user.styles
    : isError
      ? { content: ERROR_BUBBLE_CONTENT_STYLE }
      : ASSISTANT_BUBBLE_STYLES;

  // Bubble.List pads every .ant-bubble — zero first item's block-start only.
  const firstFlushStyles =
    idx === 0
      ? {
          ...roleStyles,
          root: {
            ...('root' in roleStyles ? roleStyles.root : {}),
            paddingBlockStart: 0,
          },
        }
      : isError
        ? { content: ERROR_BUBBLE_CONTENT_STYLE }
        : undefined;

  return {
    key,
    role,
    content: (
      <DocumentChatMessageItem
        message={message}
        isUser={isUser}
        isError={isError}
        actions={doneActions}
      />
    ),
    footerPlacement: isUser ? ('outer-end' as const) : ('outer-start' as const),
    footer: isError
      ? () => (
          <DocumentChatMessageActions
            content={message.content}
            disabled={senderBusy}
            onRetry={() => {
              onRetryError(idx);
            }}
          />
        )
      : undefined,
    ...(firstFlushStyles ? { styles: firstFlushStyles } : {}),
  };
}

function DocumentChatMessagesList({
  messages,
  senderBusy,
  autoScroll = false,
  onRetryError,
  onRegenerate,
  bottomRef,
}: Props) {
  const hasPending = messages.some((m) => m.status === 'Pending' && !m.isError);
  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === AiChatDocumentMessagePayloadRoleEnum.Assistant) {
        return i;
      }
    }
    return -1;
  }, [messages]);

  const itemOpts = useMemo(
    () => ({
      hasPending,
      senderBusy,
      lastAssistantIndex,
      onRetryError,
      onRegenerate,
    }),
    [hasPending, senderBusy, lastAssistantIndex, onRetryError, onRegenerate],
  );

  const items = useMemo(
    () => messages.map((message, idx) => buildBubbleItem(message, idx, itemOpts)),
    [messages, itemOpts],
  );

  // ponytail: no Virtuoso — Bubble.List has no built-in virtualization either;
  // parent scroll + stickToBottomRef owns stickiness (autoScroll only when stick).
  return (
    <div className={clsx('flex w-full flex-col pb-0')} data-testid="document-chat-messages-list">
      <Bubble.List
        autoScroll={autoScroll}
        className="w-full!"
        style={{ width: '100%' }}
        role={bubbleRoleStyles}
        items={items}
      />
      {bottomRef ? <div ref={bottomRef} /> : null}
    </div>
  );
}

export default memo(DocumentChatMessagesList);
